import { memo, type ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { UserAvatar } from '@/components';
import { sanitizeUserInput, formatUserName } from '@/lib';
import type { UserProfile } from '../model';

interface UserSearchCardProps {
  /** Данные пользователя */
  user: UserProfile;
  /** Слот для кнопки действия */
  actionSlot?: ReactNode;
  /** Callback при клике на карточку */
  onClick?: () => void;
  /** Callback для перехода на профиль пользователя */
  onUserClick?: (userId: string) => void;
  /** Функция перевода */
  t: (key: string) => string;
}

/**
 * Компонент карточки пользователя в результатах поиска
 * Использует тот же дизайн, что и FriendCard для консистентности
 */
export const UserSearchCard = memo(function UserSearchCard({ user, actionSlot, onClick, onUserClick, t }: UserSearchCardProps): JSX.Element {
  // Sanitize UGC для защиты от XSS
  const safeDisplayName = sanitizeUserInput(user.displayName);
  const safeLastName = sanitizeUserInput(user.lastName);
  const safeUsername = sanitizeUserInput(user.username);
  
  // Сокращённый формат: "Имя Ф." — для компактных карточек
  const displayName = formatUserName(safeDisplayName, safeLastName, { short: true }) || safeUsername || t('common:noResults');
  
  // Определяем подзаголовок: username если есть display_name
  const subtitle = safeDisplayName && safeUsername ? `@${safeUsername}` : undefined;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (onUserClick) {
      onUserClick(user.id);
    }
  };

  return (
    <button type="button" onClick={handleClick} className="text-left w-full">
      <Card 
        className="p-4 gap-0 flex-row items-center active:scale-[0.98] transition-transform"
      >
        <UserAvatar
          userId={user.id}
          avatarUrl={user.avatarUrl}
          displayName={user.displayName}
          lastName={user.lastName}
          username={user.username}
          size="default"
          className="flex-shrink-0"
        />
        
        <div className="flex-1 min-w-0 ml-3.5">
          <p className="text-base text-foreground truncate">
            {displayName}
          </p>
          {subtitle && (
            <p className="text-sm text-muted-foreground font-normal truncate">
              {subtitle}
            </p>
          )}
        </div>
        
        {actionSlot && (
          // Обёртка для actionSlot — останавливает всплытие клика/клавиш на родительскую карточку
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- это обёртка для stopPropagation, сам actionSlot имеет свою клавиатурную поддержку
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {actionSlot}
          </div>
        )}
      </Card>
    </button>
  );
});